import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Eye, EyeOff, Cloud, CheckCircle2, XCircle, Lock, Unlock, Mail, Send, RefreshCw, Sparkles, Shield, ShieldOff } from "lucide-react";
import api, { formatErr } from "../../lib/api";

const SesGroup = ({ s, setS, save }) => {
  const [show, setShow] = useState(false);
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flushBusy, setFlushBusy] = useState(false);

  const onTest = async () => {
    setBusy(true); setTest(null);
    try {
      await save(["aws_ses_access_key", "aws_ses_secret_key", "aws_ses_region", "aws_ses_from_email"]);
      const r = await api.post("/developer/ses/test");
      setTest(r.data);
      if (r.data.ok) toast.success(`SES connected — ${r.data.from} (Quota: ${r.data.sent_last_24h}/${r.data.max_24h})`);
      else toast.error(r.data.error || "SES connection failed");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const onFlush = async () => {
    setFlushBusy(true);
    try {
      const r = await api.post("/developer/ses/flush");
      toast.success(`Flushed: ${r.data.sent} sent · ${r.data.failed} failed`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setFlushBusy(false); }
  };

  return (
    <div className="card-luxe p-6 md:col-span-2">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Mail size={18} className="text-copper"/>
          <div>
            <div className="overline">AWS SES — Transactional Email</div>
            <div className="text-xs text-white/50 mt-0.5">Sends OTPs, order confirmations, account alerts. When configured, on-screen OTP hints are disabled and all emails route via SES.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {test && (
            <span className={`text-xs uppercase tracking-luxe flex items-center gap-1.5 ${test.ok ? "text-[#32D74B]" : "text-red-400"}`}>
              {test.ok ? <CheckCircle2 size={14}/> : <XCircle size={14}/>}
              {test.ok ? "Connection OK" : "Failed"}
            </span>
          )}
          <button onClick={() => setShow(!show)} className="text-white/50 hover:text-white text-xs flex items-center gap-1">{show ? <EyeOff size={13}/> : <Eye size={13}/>} {show ? "Hide" : "Reveal"}</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Access Key ID</div>
          <input data-testid="key-aws_ses_access_key" type={show ? "text" : "password"} className="input-box font-mono text-sm" value={s.aws_ses_access_key || ""} onChange={(e) => setS({ ...s, aws_ses_access_key: e.target.value })}/>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Secret Access Key</div>
          <input data-testid="key-aws_ses_secret_key" type={show ? "text" : "password"} className="input-box font-mono text-sm" value={s.aws_ses_secret_key || ""} onChange={(e) => setS({ ...s, aws_ses_secret_key: e.target.value })}/>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Region (e.g. us-east-1, eu-west-1)</div>
          <input data-testid="key-aws_ses_region" className="input-box font-mono text-sm" value={s.aws_ses_region || ""} onChange={(e) => setS({ ...s, aws_ses_region: e.target.value })}/>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Verified From Email (must be SES-verified)</div>
          <input data-testid="key-aws_ses_from_email" className="input-box font-mono text-sm" placeholder="orders@yourdomain.com" value={s.aws_ses_from_email || ""} onChange={(e) => setS({ ...s, aws_ses_from_email: e.target.value })}/>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-5">
        <button data-testid="save-AWS-SES" onClick={() => save(["aws_ses_access_key", "aws_ses_secret_key", "aws_ses_region", "aws_ses_from_email"])} className="btn-primary"><Save size={13}/> Save SES Settings</button>
        <button data-testid="test-ses" onClick={onTest} disabled={busy} className="btn-outline">{busy ? "Testing…" : <><Send size={12}/> Save & Test Connection</>}</button>
        <button data-testid="flush-ses" onClick={onFlush} disabled={flushBusy} className="btn-outline" title="Send all queued/failed emails through SES now">
          {flushBusy ? "Flushing…" : <><RefreshCw size={12}/> Flush Email Queue</>}
        </button>
      </div>
      {test && !test.ok && (
        <div className="mt-3 text-xs text-red-400 font-mono">{test.error}</div>
      )}
      <div className="mt-4 text-[11px] text-white/40 leading-relaxed">
        <div className="overline mb-1">Setup tip</div>
        Verify your sender domain or email address in the AWS SES console first. New AWS accounts start in <em>sandbox</em> mode — only verified addresses can receive email until you request production access.
      </div>
    </div>
  );
};

const KeyGroup = ({ title, fields, vals, setVals, save, footer }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="card-luxe p-6">
      <div className="flex justify-between items-center mb-5">
        <div className="overline">{title}</div>
        <button onClick={() => setShow(!show)} className="text-white/50 hover:text-white text-xs flex items-center gap-1">{show ? <EyeOff size={13}/> : <Eye size={13}/>} {show ? "Hide" : "Reveal"}</button>
      </div>
      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.k}>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">{f.l}</div>
            <input data-testid={`key-${f.k}`} type={f.public || show ? "text" : "password"} className="input-box font-mono text-sm" value={vals[f.k] || ""} onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })}/>
          </div>
        ))}
      </div>
      {footer}
      <button data-testid={`save-${title}`} onClick={save} className="btn-primary mt-5"><Save size={13}/> Save</button>
    </div>
  );
};

export default function DevKeys() {
  const [s, setS] = useState({});
  const [s3Test, setS3Test] = useState(null);
  const [testingS3, setTestingS3] = useState(false);

  useEffect(() => { api.get("/developer/settings").then((r) => setS(r.data)); }, []);

  const save = async (keys) => {
    try {
      const patch = {}; keys.forEach((k) => (patch[k] = s[k] ?? ""));
      await api.put("/developer/settings", patch);
      toast.success("Saved");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const togglePaymentsLock = async () => {
    try {
      const next = !s.payments_disabled;
      const r = await api.put("/developer/payments-lock", { on: next, message: s.payments_disabled_message });
      setS({ ...s, ...r.data });
      toast.success(next ? "Payments are now LOCKED — checkout will be greyed out for customers." : "Payments unlocked. Checkout fully active.");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleAiLock = async () => {
    try {
      // 'on:true' means "lock turned ON" → disable AI
      const turnOn = !!s.ai_image_generation_enabled;
      const r = await api.put("/developer/ai-lock", { on: turnOn });
      setS({ ...s, ai_image_generation_enabled: r.data.ai_image_generation_enabled });
      toast.success(r.data.ai_image_generation_enabled ? "AI image generation enabled" : "AI image generation LOCKED for all users");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setAiProvider = async (provider) => {
    try {
      const r = await api.put("/developer/ai-provider", { provider });
      setS({ ...s, ai_provider: r.data.ai_provider });
      toast.success(`AI provider switched to ${provider.toUpperCase()}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const [leoTest, setLeoTest] = useState(null);
  const [leoBusy, setLeoBusy] = useState(false);
  const testLeo = async () => {
    setLeoBusy(true); setLeoTest(null);
    try {
      await save(["leonardo_api_key", "leonardo_model_id", "leonardo_cost_per_token_usd", "leonardo_token_to_aed_rate"]);
      const r = await api.post("/developer/leonardo/test");
      setLeoTest(r.data);
      if (r.data.ok) toast.success(`Leonardo connected — ${r.data.tokens_remaining} tokens remaining.`);
      else toast.error(r.data.error || "Leonardo connection failed");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLeoBusy(false); }
  };

  const testS3 = async () => {
    setTestingS3(true); setS3Test(null);
    try {
      // Save first to ensure latest values are tested
      await save(["s3_access_key", "s3_secret_key", "s3_region", "s3_bucket", "s3_endpoint_url", "s3_public_base_url"]);
      const r = await api.post("/developer/s3/test");
      setS3Test(r.data);
      if (r.data.ok) toast.success(`S3 connected — bucket “${r.data.bucket}” is reachable.`);
      else toast.error(r.data.error || "S3 connection failed");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setTestingS3(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="overline mb-2">Integrations</div>
        <h1 className="font-display text-4xl">API Keys & Credentials</h1>
        <p className="text-sm text-white/50 mt-2">Configure third-party services. Keys are stored privately and never exposed to other roles.</p>
      </div>

      {/* Payment Mode + Lock */}
      <div className="card-luxe p-5">
        <div className="overline mb-3">Payment Mode</div>
        <div className="flex gap-3 flex-wrap items-center">
          {["test", "live"].map((m) => {
            const selected = s.payment_mode === m;
            return (
              <button
                key={m}
                data-testid={`mode-${m}`}
                onClick={() => setS({ ...s, payment_mode: m })}
                className={`px-5 py-2 text-xs uppercase tracking-luxe border transition-colors ${selected ? "border-copper" : "border-white/15 text-white/70 hover:border-copper/60"}`}
                style={selected ? { backgroundColor: "#B89766", color: "#fff", borderColor: "#B89766" } : undefined}
              >
                {m}
              </button>
            );
          })}
          <button onClick={() => save(["payment_mode"])} className="btn-primary ml-auto"><Save size={13}/> Save Mode</button>
        </div>

        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1 min-w-[260px]">
              <div className="overline mb-1">Payments Lock — Storefront</div>
              <p className="text-xs text-white/55">Grey out the checkout payment step. Customers can still browse, add to cart and fill contact + shipping details, but order placement is blocked. Use this until your live payment keys are wired.</p>
            </div>
            <button
              data-testid="payments-lock-toggle"
              onClick={togglePaymentsLock}
              className={`px-5 py-2.5 text-xs uppercase tracking-luxe border flex items-center gap-2 transition-colors`}
              style={s.payments_disabled
                ? { backgroundColor: "#DC2626", color: "#fff", borderColor: "#DC2626" }
                : { backgroundColor: "transparent", color: "#32D74B", borderColor: "rgba(50,215,75,0.35)" }
              }
            >
              {s.payments_disabled ? (<><Lock size={13}/> LOCKED</>) : (<><Unlock size={13}/> Active</>)}
            </button>
          </div>
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Locked-state Message (shown to customers)</div>
            <textarea data-testid="payments-lock-message" className="input-box" rows={2} value={s.payments_disabled_message || ""} onChange={(e) => setS({ ...s, payments_disabled_message: e.target.value })}/>
            <button onClick={() => save(["payments_disabled_message"])} className="btn-outline mt-2 text-xs"><Save size={12}/> Save Message</button>
          </div>
        </div>
      </div>

      {/* AI Image Generation — Master Lock + Provider + Leonardo */}
      <div className="card-luxe p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Sparkles size={18} className="text-copper"/>
            <div>
              <div className="overline">AI Image Generation</div>
              <div className="text-xs text-white/50 mt-0.5">Master switch + provider settings. When LOCKED, all variation calls are blocked across roles.</div>
            </div>
          </div>
          <button
            data-testid="ai-lock-toggle"
            onClick={toggleAiLock}
            className="px-5 py-2.5 text-xs uppercase tracking-luxe border flex items-center gap-2 transition-colors"
            style={!s.ai_image_generation_enabled
              ? { backgroundColor: "#DC2626", color: "#fff", borderColor: "#DC2626" }
              : { backgroundColor: "transparent", color: "#32D74B", borderColor: "rgba(50,215,75,0.35)" }
            }
          >
            {s.ai_image_generation_enabled ? <><ShieldOff size={13}/> Active</> : <><Shield size={13}/> LOCKED</>}
          </button>
        </div>

        <div className="overline mb-2">Active Provider</div>
        <div className="flex gap-2 flex-wrap mb-5">
          {[
            { id: "gemini", label: "Gemini Nano Banana (Emergent)" },
            { id: "leonardo", label: "Leonardo.AI (paid)" },
          ].map((opt) => {
            const selected = (s.ai_provider || "gemini") === opt.id;
            return (
              <button
                key={opt.id}
                data-testid={`ai-provider-${opt.id}`}
                onClick={() => setAiProvider(opt.id)}
                className="px-4 py-2 text-xs uppercase tracking-luxe border transition-colors"
                style={selected
                  ? { backgroundColor: "#B89766", color: "#fff", borderColor: "#B89766" }
                  : { backgroundColor: "transparent", color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.15)" }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="overline mb-3">Leonardo.AI Configuration</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">API Key</div>
            <input data-testid="key-leonardo_api_key" type="password" className="input-box font-mono text-sm" value={s.leonardo_api_key || ""} onChange={(e) => setS({ ...s, leonardo_api_key: e.target.value })}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Model ID (Leonardo Phoenix is default)</div>
            <input data-testid="key-leonardo_model_id" className="input-box font-mono text-sm" value={s.leonardo_model_id || ""} onChange={(e) => setS({ ...s, leonardo_model_id: e.target.value })}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Cost per token (USD)</div>
            <input data-testid="key-leonardo_cost_per_token_usd" type="number" step="0.0001" className="input-box font-mono text-sm" value={s.leonardo_cost_per_token_usd || ""} onChange={(e) => setS({ ...s, leonardo_cost_per_token_usd: parseFloat(e.target.value) })}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Token → AED rate</div>
            <input data-testid="key-leonardo_token_to_aed_rate" type="number" step="0.0001" className="input-box font-mono text-sm" value={s.leonardo_token_to_aed_rate || ""} onChange={(e) => setS({ ...s, leonardo_token_to_aed_rate: parseFloat(e.target.value) })}/>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <button data-testid="save-Leonardo" onClick={() => save(["leonardo_api_key", "leonardo_model_id", "leonardo_cost_per_token_usd", "leonardo_token_to_aed_rate"])} className="btn-primary"><Save size={13}/> Save Leonardo Settings</button>
          <button data-testid="test-leonardo" onClick={testLeo} disabled={leoBusy} className="btn-outline">{leoBusy ? "Testing…" : <><Sparkles size={12}/> Save & Test Connection</>}</button>
        </div>
        {leoTest && leoTest.ok && (
          <div className="mt-3 text-xs text-[#32D74B] font-mono">✓ {leoTest.username} · {leoTest.tokens_remaining} tokens remaining · plan: {leoTest.plan || "—"}</div>
        )}
        {leoTest && !leoTest.ok && (
          <div className="mt-3 text-xs text-red-400 font-mono">{leoTest.error}</div>
        )}
        <div className="mt-4 text-[11px] text-white/40 leading-relaxed">
          <div className="overline mb-1">Setup tip</div>
          Get your key from <code className="text-copper">https://app.leonardo.ai/api-access</code>. Phoenix Lightning model is fastest (≈4-6s per image). Cost is approximate — adjust based on your plan's tokens/USD ratio.
        </div>
      </div>

      {/* Image Storage — AWS S3 */}
      <div className="card-luxe p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Cloud size={18} className="text-copper"/>
            <div>
              <div className="overline">Image Storage — AWS S3</div>
              <div className="text-xs text-white/50 mt-0.5">When configured, hero/curated/logo/chatbot images upload to S3. Without keys, images stay as base64 in MongoDB.</div>
            </div>
          </div>
          {s3Test && (
            <span className={`text-xs uppercase tracking-luxe flex items-center gap-1.5 ${s3Test.ok ? "text-[#32D74B]" : "text-red-400"}`}>
              {s3Test.ok ? <CheckCircle2 size={14}/> : <XCircle size={14}/>}
              {s3Test.ok ? "Connection OK" : "Failed"}
            </span>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Access Key ID</div>
            <input data-testid="key-s3_access_key" type="password" className="input-box font-mono text-sm" value={s.s3_access_key || ""} onChange={(e) => setS({ ...s, s3_access_key: e.target.value })}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Secret Access Key</div>
            <input data-testid="key-s3_secret_key" type="password" className="input-box font-mono text-sm" value={s.s3_secret_key || ""} onChange={(e) => setS({ ...s, s3_secret_key: e.target.value })}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Region (e.g. us-east-1, ap-south-1)</div>
            <input data-testid="key-s3_region" className="input-box font-mono text-sm" value={s.s3_region || ""} onChange={(e) => setS({ ...s, s3_region: e.target.value })}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Bucket Name</div>
            <input data-testid="key-s3_bucket" className="input-box font-mono text-sm" value={s.s3_bucket || ""} onChange={(e) => setS({ ...s, s3_bucket: e.target.value })}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Custom Endpoint URL (optional — for R2/MinIO)</div>
            <input data-testid="key-s3_endpoint_url" className="input-box font-mono text-sm" placeholder="https://<account>.r2.cloudflarestorage.com" value={s.s3_endpoint_url || ""} onChange={(e) => setS({ ...s, s3_endpoint_url: e.target.value })}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Public Base URL (optional — CDN/CloudFront)</div>
            <input data-testid="key-s3_public_base_url" className="input-box font-mono text-sm" placeholder="https://cdn.yoursite.com" value={s.s3_public_base_url || ""} onChange={(e) => setS({ ...s, s3_public_base_url: e.target.value })}/>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <button data-testid="save-S3" onClick={() => save(["s3_access_key","s3_secret_key","s3_region","s3_bucket","s3_endpoint_url","s3_public_base_url"])} className="btn-primary"><Save size={13}/> Save S3 Settings</button>
          <button data-testid="test-s3" onClick={testS3} disabled={testingS3} className="btn-outline">{testingS3 ? "Testing…" : "Save & Test Connection"}</button>
        </div>
        {s3Test && !s3Test.ok && (
          <div className="mt-3 text-xs text-red-400 font-mono">{s3Test.error}</div>
        )}
        <div className="mt-4 text-[11px] text-white/40 leading-relaxed">
          <div className="overline mb-1">Setup tip</div>
          Create an IAM user with <code className="text-copper">s3:PutObject</code>, <code className="text-copper">s3:GetObject</code> on your bucket. Add a bucket policy allowing public read, and a CORS rule allowing your storefront domain.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <KeyGroup title="Stripe (Test)" fields={[{ k: "stripe_test_pk", l: "Publishable Key (pk_test_…)", public: true }, { k: "stripe_test_sk", l: "Secret Key (sk_test_…)" }]} vals={s} setVals={setS} save={() => save(["stripe_test_pk", "stripe_test_sk"])}/>
        <KeyGroup title="Stripe (Live)" fields={[{ k: "stripe_live_pk", l: "Publishable Key (pk_live_…)", public: true }, { k: "stripe_live_sk", l: "Secret Key (sk_live_…)" }]} vals={s} setVals={setS} save={() => save(["stripe_live_pk", "stripe_live_sk"])}/>
        <KeyGroup title="PayPal (Sandbox)" fields={[{ k: "paypal_sandbox_client_id", l: "Client ID" }, { k: "paypal_sandbox_secret", l: "Secret" }]} vals={s} setVals={setS} save={() => save(["paypal_sandbox_client_id", "paypal_sandbox_secret"])}/>
        <KeyGroup title="PayPal (Live)" fields={[{ k: "paypal_live_client_id", l: "Client ID" }, { k: "paypal_live_secret", l: "Secret" }]} vals={s} setVals={setS} save={() => save(["paypal_live_client_id", "paypal_live_secret"])}/>
        <SesGroup s={s} setS={setS} save={save}/>
        <KeyGroup title="DHL (Live Tracking)" fields={[{ k: "dhl_api_key", l: "DHL API Key" }, { k: "dhl_account_number", l: "Account Number", public: true }, { k: "dhl_env", l: "Environment (sandbox/production)", public: true }]} vals={s} setVals={setS} save={() => save(["dhl_api_key", "dhl_account_number", "dhl_env"])}/>
      </div>

      <div className="card-luxe p-6">
        <div className="overline mb-2">Gemini (AI Image)</div>
        <p className="text-sm text-white/50 mb-3">Powered by Emergent Universal Key — already configured. Manage usage limits in Limits & Settings.</p>
        <div className="text-xs text-[#32D74B] uppercase tracking-luxe">● Active</div>
      </div>
    </div>
  );
}
